import classNames from "classnames";

import { GridLabelProps, ResponsiveRadar } from '@nivo/radar'
import { PropsWithClassName } from 'shared/props';
import { ContentPanel, ContentPanelInteraction, EtfLabel } from 'shared/ui-kit';

import { FactorAnalsysisBackendMovement, FactorAnalsysisBackendMovementEntry } from "api/analysis";

import styles from "./correlation.module.scss";
import { useMemo } from "react";
import { animated } from '@react-spring/web'
import { useSiteResolution, decodeSiteResolutionValue } from "shared/mechanics/site-resolution";

import { createHelpHref } from '../utils';

var color1: string = "#1B9E77";
var color2: string = "#D95F02";

export interface EtfFactorAnalysisCorrelationProps {
	ticker: string;
	movement: FactorAnalsysisBackendMovement;
}
export const EtfFactorAnalysisCorrelation = (props: EtfFactorAnalysisCorrelationProps & PropsWithClassName) => {
	const groups = useMemo(() => {
		const group1Tickers = ["^SP500TR", "VTV", "VUG", "MCFTR", "RUABITR", "RTSTR", "AGG"];
		const group2Tickers = [""];

		var group1MovementEntries = props.movement.entries.filter(entry => group1Tickers.indexOf(entry.compareTicker) !== -1);
		var group2MovementEntries = props.movement.entries.filter(entry => group2Tickers.indexOf(entry.compareTicker) !== -1);
		var group3MovementEntries = props.movement.entries.filter(entry => group1Tickers.indexOf(entry.compareTicker) === -1 && group2Tickers.indexOf(entry.compareTicker) === -1);

		var group1 = { name: 'group1', movementEntries: group1MovementEntries };
		var group2 = { name: 'group2', movementEntries: group2MovementEntries };
		var groupOther = { name: 'groupOther', movementEntries: group3MovementEntries };

		return [group1, group2, groupOther].filter(x => x.movementEntries.length !== 0);
	}, [props.movement])

	return (
		<ContentPanel title={<EtfLabel ticker={props.ticker} suffix="Влияние факторов" />} className={props.className} help={{ href: createHelpHref("correlation") }}>
			<ContentPanelInteraction className={styles.interaction}>
				<LegendItem text={`Корреляция ${props.ticker}`} color={color1} />
				<LegendItem text={`Корреляция альтернативных`} color={color2} />
			</ContentPanelInteraction>
			<div className={classNames("flex-size-fill", "flex-container-horizontal", "flex-container-horizontal-break-1000", styles.container)}>
				{
					groups.map(group => (
						<div key={group.name} className="flex-size-fill">
							<EtfFactorAnalysisCorrelationRadar ticker={props.ticker} movementEntries={group.movementEntries} />
						</div>)
					)
				}
			</div>
		</ContentPanel>
	);
}

interface LegendItemProps {
	text: string;
	color: string;
}
const LegendItem = (props: LegendItemProps) => {
	return (
		<span className={styles.legend_item}>
			<i style={{ backgroundColor: props.color }}></i>
			{props.text}
		</span>
	)
}

interface EtfFactorAnalysisCorrelationRadarProps {
	ticker: string;
	movementEntries: FactorAnalsysisBackendMovementEntry[];
}
const EtfFactorAnalysisCorrelationRadar = (props: EtfFactorAnalysisCorrelationRadarProps) => {
	const siteResolution = useSiteResolution();

	const params = decodeSiteResolutionValue(
		siteResolution,
		{ containerStyle: { width: '500px', height: '300px', margin: '0 auto' }, chartMargin: { top: 20, right: 80, bottom: 20, left: 80 } },
		{ containerStyle: { width: '350px', height: '300px', margin: '0 auto' }, chartMargin: { top: 20, right: 80, bottom: 20, left: 80 } });

	const layer1 = `Корреляция ${props.ticker}`;
	const layer2 = `Корреляция альтернативных`;

	const data = props.movementEntries.map(entry => ({
		"name": entry.compareDescription,
		[layer1]: Math.abs(entry.correlation),
		[layer2]: entry.averageAlternativeCorrelation
	}));

	return (
		<div style={params.containerStyle}>
			<ResponsiveRadar
				data={data}
				keys={[layer2, layer1]}
				indexBy="name"
				valueFormat=">-.2f"
				margin={params.chartMargin}
				maxValue={1}
				borderWidth={2}
				borderColor={{ from: 'color' }}
				gridLabelOffset={10}
				dotSize={5}
				dotColor={{ theme: 'background' }}
				dotBorderWidth={1}
				colors={[color2, color1]}
				gridLevels={4}
				gridShape="linear"
				motionConfig="wobbly"
				gridLabel={ResponsiveRadarLabel}
				theme={{
					grid: {
						line: {
							stroke: '#7D7B97'
						}
					},
					tooltip: {
						container: {
							background: "#000",
							color: "#ddd",
							fontSize: "13px"
						}
					},
					fontSize: 10,
					textColor: "#F5F5F5"
				}}
			/>
		</div>
	)
}

const ResponsiveRadarLabel = (props: GridLabelProps) => {
	var lines = props.id.split(' ');
	var dy = "1.2em"
	if (props.anchor == "middle") {
		dy = "-1.2em";
		lines = lines.reverse();
	}

	return (
		<animated.g transform={props.animated.transform}>
			<text fill='#F5F5F5' style={{ color: 'write' }} dominantBaseline="central" textAnchor={props.anchor}>
				{
					lines.map((line, i) => <tspan key={line} x="0" dy={i === 0 ? "0" : dy}>{line}</tspan>)
				}
			</text>
		</animated.g>
	);
}